import { useEffect, useMemo, useState } from 'react'

import { RubricArtifactCanvasLayout } from '@/components/admin/evaluation-rubrics/rubric-artifact-canvas-layout'
import { ArtifactCanvasSaveActions } from '@/components/artifact/artifact-canvas-layout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SCORING_PRESETS,
  type ScoringPresetKey,
} from '@/lib/rubric-publish-payload'
import type { ScoringConfig } from '@/components/admin/evaluation-rubrics/rubric-read-helpers'

type RubricScoringCanvasProps = {
  scoring: ScoringConfig
  scoringPreset: ScoringPresetKey | 'custom'
  onSave: (scoring: ScoringConfig, preset: ScoringPresetKey | 'custom') => void
  onClose: () => void
}

export function RubricScoringCanvas({
  scoring,
  scoringPreset,
  onSave,
  onClose,
}: RubricScoringCanvasProps) {
  const [draftPreset, setDraftPreset] = useState(scoringPreset)
  const [draftScoring, setDraftScoring] = useState(scoring)

  useEffect(() => {
    setDraftPreset(scoringPreset)
    setDraftScoring(scoring)
  }, [scoring, scoringPreset])

  const isDirty = useMemo(
    () =>
      JSON.stringify(draftScoring) !== JSON.stringify(scoring)
      || draftPreset !== scoringPreset,
    [draftPreset, draftScoring, scoring, scoringPreset],
  )

  function applyPreset(key: ScoringPresetKey | 'custom') {
    setDraftPreset(key)
    if (key !== 'custom') {
      const preset = SCORING_PRESETS[key]
      setDraftScoring({
        structured_weight: preset.structured_weight,
        semantic_weight: preset.semantic_weight,
        llm_threshold: preset.llm_threshold,
      })
    }
  }

  return (
    <RubricArtifactCanvasLayout
      actions={
        <ArtifactCanvasSaveActions
          onClose={onClose}
          onSave={() => onSave(draftScoring, draftPreset)}
          isDirty={isDirty}
          isSaving={false}
        />
      }
    >
      <p className="text-sm text-muted-foreground">
        Controls how structured skill overlap vs semantic similarity are blended, and when deep AI
        judging runs.
      </p>
      <div className="max-w-sm space-y-2">
        <Label>Preset</Label>
        <Select
          value={draftPreset}
          onValueChange={(value) => applyPreset(value as ScoringPresetKey | 'custom')}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(
              Object.entries(SCORING_PRESETS) as Array<
                [ScoringPresetKey, typeof SCORING_PRESETS.balanced]
              >
            ).map(([key, preset]) => (
              <SelectItem key={key} value={key}>
                {preset.label}
              </SelectItem>
            ))}
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid max-w-xl gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label>Structured blend</Label>
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draftScoring.structured_weight ?? ''}
            onChange={(event) => {
              setDraftPreset('custom')
              setDraftScoring((current) => ({
                ...current,
                structured_weight: Number(event.target.value),
              }))
            }}
          />
        </div>
        <div className="space-y-1">
          <Label>Semantic blend</Label>
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draftScoring.semantic_weight ?? ''}
            onChange={(event) => {
              setDraftPreset('custom')
              setDraftScoring((current) => ({
                ...current,
                semantic_weight: Number(event.target.value),
              }))
            }}
          />
        </div>
        <div className="space-y-1">
          <Label>Deep AI threshold</Label>
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draftScoring.llm_threshold ?? ''}
            onChange={(event) => {
              setDraftPreset('custom')
              setDraftScoring((current) => ({
                ...current,
                llm_threshold: Number(event.target.value),
              }))
            }}
          />
        </div>
      </div>
    </RubricArtifactCanvasLayout>
  )
}
